/****************************************************
 * Module Name:  CustomerApiSettingsModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { Box, TextField, FormControlLabel, Switch, Button } from "@mui/material";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import logo from '../../images/pansuriya.jpg';

import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import { styled } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';
import LocationProxy from "../../utility/proxy/manager/locationProxy";
import Select from "react-select";
import UserPermissionProxy from "../../utility/proxy/manager/permissionProxy/userPermissionProxy";

const NewStoneSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color:
                theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[600],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

const CustomerApiSettingsModal = ({
    customerid,
    Show,
    handleClose,
    handleSave,
    loading,
    editOperation,
    sourceData,
}) => {
    const [showError, setShowError] = React.useState(false);
    const [allowApi, setAllowApi] = useState(editOperation ? sourceData.allowapi : false);
    const [holdRelease, setHoldRelease] = useState(editOperation ? sourceData.holdrelease : false);
    const [holdReleaseMax, setHoldReleaseMax] = useState(editOperation ? sourceData.holdreleasemax : 0);
    const [myHoldMax, setMyHoldMax] = useState(editOperation ? sourceData.myholdmax : 0);
    const [myOfferMax, setMyOfferMax] = useState(editOperation ? sourceData.myoffermax : 0);
    const [mySellMax, setMySellMax] = useState(editOperation ? sourceData.mysellmax : 0);
    const [offer, setOffer] = useState(editOperation ? sourceData.offer : false);
    const [offerMax, setOfferMax] = useState(editOperation ? sourceData.offermax : 0);
    const [sell, setSell] = useState(editOperation ? sourceData.sell : false);
    const [sellMax, setSellMax] = useState(editOperation ? sourceData.sellmax : 0);
    const [stockList, setStockList] = useState(editOperation ? sourceData.stocklist : false);
    const [stockListMax, setStockListMax] = useState(editOperation ? sourceData.stocklistmax : 0);
    const [newStatus, setNewStatus] = useState(editOperation ? sourceData.status : "");
    const [newStoneType, setNewStoneType] = useState(editOperation ? sourceData.stonetype : "");
    const [newLocation, setNewLocation] = useState(editOperation ? sourceData.location : "");
    const [allStatusOptions, setAllStatusOptions] = useState([]);
    const [allStoneTypeOptions, setAllStoneTypeOptions] = useState([]);
    const [allLocationOptions, setAllLocationOptions] = useState([]);
    const [stoneTypeMismatch, setStoneTypeMismatch] = useState(false);

    useEffect(() => {
        getStatusOptions();
        getStoneTypeOptions();
        getLocationOptions();
    }, []);

    const getStatusOptions = () => {
        const optionsArray = Constants.STONE_STATUS.map((status) => ({
            value: status.value,
            label: status.label,
        }));

        const statusArray = newStatus.split(',').map(item => item.trim());
        const selectedOptions = optionsArray.filter((option) => {
            return statusArray.includes(option.value);
        });

        setAllStatusOptions(optionsArray);
        setNewStatus(selectedOptions.length > 0 ? selectedOptions : [optionsArray[0]]);
    }

    const getStoneTypeOptions = () => {
        let userId = [customerid];
        UserPermissionProxy.GetSingleUserStoneTypePermission(userId, (response) => {
            const permissionMap = new Map();
            response.forEach((perm) => {
                permissionMap.set(perm.permissionid, perm.permissionname.replace("STONETYPE_", ""),);
            });

            const optionsArray = [];

            if (permissionMap.size === 0) {
                Object.entries(Constants.STONE_TYPE).forEach(([key, val]) => {
                    if (val.toUpperCase() === "NATURAL") {
                        optionsArray.push({ value: key, label: key });
                    }
                });
            }
            if (permissionMap.size > 1) {
                const allValues = Object.keys(Constants.STONE_TYPE);
                optionsArray.push({ value: allValues, label: "-- ANY --" });
                Object.entries(Constants.STONE_TYPE).forEach(([key, val]) => {
                    optionsArray.push({ value: key, label: key });
                });
            } else {
                const label = Array.from(permissionMap.values())[0];
                if (label === "NATURAL") {
                    Object.entries(Constants.STONE_TYPE).forEach(([key, val]) => {
                        if (val.toUpperCase() === "NATURAL") {
                            optionsArray.push({ value: key, label: key });
                        }
                    });
                } else if (label === "LABGROWN") {
                    Object.entries(Constants.STONE_TYPE).forEach(([key, val]) => {
                        if (["CVD", "HPHT"].includes(val.toUpperCase())) {
                            optionsArray.push({ value: key, label: key });
                        }
                    });
                }
            }

            const stoneTypeArray = newStoneType.split(',').map(item => item.trim());
            const selectedOptions = optionsArray.filter((option) => {
                return stoneTypeArray.includes(option.value);
            });

            if (newStoneType && newStoneType.trim() !== "") {
                // Check for mismatch between DB values and permission-based values
                const permissionValues = optionsArray.map(opt => opt.value).flat();
                const hasMismatch = stoneTypeArray.some(type => !permissionValues.includes(type));
                setStoneTypeMismatch(hasMismatch);
            }
            setAllStoneTypeOptions(optionsArray);
            setNewStoneType(selectedOptions.length > 0 ? selectedOptions : [optionsArray[0]]);
        });
    };


    const getLocationOptions = () => {
        LocationProxy.GetAllLocation((response) => {
            if (response && Array.isArray(response)) {
                const allValues = response.map((stone) => stone.name);

                const optionsArray = [
                    { value: allValues, label: "-- ANY --" },
                    ...response.map((stone) => ({
                        value: stone.name,
                        label: stone.name,
                    })),
                ];
                const locationArray = newLocation.split(',').map(item => item.trim());
                const selectedOptions = optionsArray.filter((option) => {
                    return locationArray.includes(option.value);
                });

                setAllLocationOptions(optionsArray);
                setNewLocation(selectedOptions.length > 0 ? selectedOptions : [optionsArray[0]]);
            }
        });
    };

    const closeErrorDialog = () => {
        setShowError(false);
    };

    const getCommaSeparatedValues = (optionsArray) => {
        if (!Array.isArray(optionsArray)) return '';
        const anyOption = optionsArray.find(option => option.label === '-- ANY --');
        if (anyOption) {
            return Array.isArray(anyOption.value)
                ? anyOption.value.join(',')
                : anyOption.value;
        }
        return optionsArray.map(option => option.value).join(',');
    };

    const saveData = () => {
        let data = {
            userid: customerid,
            allowapi: allowApi,
            holdrelease: holdRelease,
            holdreleasemax: parseInt(holdReleaseMax),
            myholdmax: parseInt(myHoldMax),
            myoffermax: parseInt(myOfferMax),
            mysellmax: parseInt(mySellMax),
            offer: offer,
            offermax: parseInt(offerMax),
            sell: sell,
            sellmax: parseInt(sellMax),
            stocklist: stockList,
            stocklistmax: parseInt(stockListMax),
            status: getCommaSeparatedValues(newStatus),
            stonetype: getCommaSeparatedValues(newStoneType),
            location: getCommaSeparatedValues(newLocation),
        };

        if (handleSave) {
            handleSave(data);
        }
    }


    return (
        <Box>
            <CommonSnackbar
                show={showError}
                message={
                    <Box>
                        Add Customer Api settings validation Failed
                        <br /> Please enter complete data
                    </Box>
                }
                variant={"error"}
                autoHide={false}
                handleClose={closeErrorDialog}
                title="Validation Failure"
            />

            <Modal backdrop="static" show={Show} onHide={handleClose} enforceFocus={false} dialogClassName="custom-modalSize1">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Add or Edit Customer Api Settings
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <center>
                        <table style={{ width: "100%" }}>
                            <tbody>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px" }}>
                                        <Form.Label>Customer Id</Form.Label>
                                    </td>
                                    <td style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <Form.Label style={{ fontWeight: "bold" }}>{customerid}</Form.Label>
                                        <FormControlLabel
                                            control={
                                                <NewStoneSwitch
                                                    sx={{ m: 1 }}
                                                    checked={allowApi}
                                                    onChange={() => setAllowApi(!allowApi)}
                                                />
                                            }
                                            label="Allow API"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px" }}>
                                        <Form.Label>Stock List</Form.Label>
                                    </td>
                                    <td style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <FormControlLabel
                                            control={
                                                <NewStoneSwitch
                                                    sx={{ m: 1 }}
                                                    checked={stockList}
                                                    onChange={() => setStockList(!stockList)}
                                                />
                                            }
                                        />
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Stock List Max"
                                            placeholder="Enter Stock List Max"
                                            value={stockListMax}
                                            onChange={(e) => setStockListMax(e.target.value)}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px" }}>
                                        <Form.Label>
                                            Hold Release
                                        </Form.Label>
                                    </td>
                                    <td style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <FormControlLabel
                                            control={
                                                <NewStoneSwitch
                                                    sx={{ m: 1 }}
                                                    checked={holdRelease}
                                                    onChange={() => setHoldRelease(!holdRelease)}
                                                />
                                            }
                                        />
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Hold Release Max"
                                            placeholder="Enter Hold Release Max"
                                            value={holdReleaseMax}
                                            onChange={(e) => setHoldReleaseMax(e.target.value)}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px" }}>
                                        <Form.Label>View My Hold</Form.Label>
                                    </td>
                                    <td style={{ display: "flex", alignItems: "center", paddingLeft: "73px" }}>
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="My Hold Max"
                                            placeholder="Enter My Hold Max"
                                            value={myHoldMax}
                                            onChange={(e) => setMyHoldMax(e.target.value)}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px" }}>
                                        <Form.Label>
                                            Offer
                                        </Form.Label>
                                    </td>
                                    <td style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <FormControlLabel
                                            control={
                                                <NewStoneSwitch
                                                    sx={{ m: 1 }}
                                                    checked={offer}
                                                    onChange={() => setOffer(!offer)}
                                                />
                                            }
                                        />
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Offer Max"
                                            placeholder="Enter Offer Max"
                                            value={offerMax}
                                            onChange={(e) => setOfferMax(e.target.value)}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px" }}>
                                        <Form.Label>
                                            View My Offer
                                        </Form.Label>
                                    </td>
                                    <td style={{ display: "flex", alignItems: "center", paddingLeft: "73px" }}>
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="My Offer Max"
                                            placeholder="Enter My Offer Max"
                                            value={myOfferMax}
                                            onChange={(e) => setMyOfferMax(e.target.value)}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px" }}>
                                        <Form.Label>
                                            Sell
                                        </Form.Label>
                                    </td>
                                    <td style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <FormControlLabel
                                            control={
                                                <NewStoneSwitch
                                                    sx={{ m: 1 }}
                                                    checked={sell}
                                                    onChange={() => setSell(!sell)}
                                                />
                                            }
                                        />
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Sell Max"
                                            placeholder="Enter Sell Max"
                                            value={sellMax}
                                            onChange={(e) => setSellMax(e.target.value)}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px" }}>
                                        <Form.Label>
                                            View My Sell
                                        </Form.Label>
                                    </td>
                                    <td style={{ display: "flex", alignItems: "center", paddingLeft: "73px" }}>
                                        <TextField
                                            margin="normal"
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="My Sell Max"
                                            placeholder="Enter My Sell Max"
                                            value={mySellMax}
                                            onChange={(e) => setMySellMax(e.target.value)}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px", paddingTop: "15px" }}>
                                        <Form.Label>
                                            Status <sup style={{ color: "red" }}>*</sup>
                                        </Form.Label>
                                    </td>
                                    <td style={{ alignItems: "center", paddingLeft: "73px", paddingTop: "15px" }}>
                                        <Select
                                            isMulti
                                            menuPlacement="auto"
                                            blurInputOnSelect={false}
                                            className="basic-single"
                                            classNamePrefix="select"
                                            value={newStatus}
                                            name="Status"
                                            styles={Constants.Styles}
                                            menuPortalTarget={document.body}
                                            options={allStatusOptions}
                                            onChange={(selectedOption) => {
                                                setNewStatus(selectedOption)
                                            }
                                            }
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px", paddingTop: "15px", verticalAlign: "top", }}>
                                        <Form.Label>
                                            Stone Type <sup style={{ color: "red" }}>*</sup>
                                        </Form.Label>
                                    </td>
                                    <td style={{ paddingLeft: "73px", paddingTop: "15px" }}>
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <Select
                                                isMulti
                                                menuPlacement="auto"
                                                blurInputOnSelect={false}
                                                className="basic-single"
                                                classNamePrefix="select"
                                                value={newStoneType}
                                                name="StoneType"
                                                styles={Constants.Styles}
                                                menuPortalTarget={document.body}
                                                options={allStoneTypeOptions}
                                                onChange={(selectedOption) => {
                                                    setNewStoneType(selectedOption);
                                                }}
                                            />
                                            {stoneTypeMismatch && (
                                                <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
                                                    Note: Some stone types from your previous selection are no longer allowed due to updated permissions. Please save again to update your preferences.
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ minWidth: "150px", paddingRight: "20px", paddingTop: "15px" }}>
                                        <Form.Label>
                                            Location <sup style={{ color: "red" }}>*</sup>
                                        </Form.Label>
                                    </td>
                                    <td style={{ alignItems: "center", paddingLeft: "73px", paddingTop: "15px" }}>
                                        <Select
                                            isMulti
                                            menuPlacement="auto"
                                            blurInputOnSelect={false}
                                            className="basic-single"
                                            classNamePrefix="select"
                                            value={newLocation}
                                            name="Location"
                                            styles={Constants.Styles}
                                            menuPortalTarget={document.body}
                                            options={allLocationOptions}
                                            onChange={(selectedOption) => {
                                                setNewLocation(selectedOption)
                                            }
                                            }
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </center>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => window.open(`${window.location.origin}/app/help/api`, '_blank')}
                        disabled={loading}
                        sx={{ mr: "228px" }}
                    >
                        View Documentation
                    </Button>
                    <LoadingButton
                        loading={loading}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                        variant="contained"
                        onClick={() => saveData()}
                        sx={{ mr: 1 }}
                    >
                        Save
                    </LoadingButton>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Box>
    );
}

export default CustomerApiSettingsModal;